import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Save, Send, X } from 'lucide-react';
import { toast } from 'sonner';

// ── Stable helper components (defined outside to prevent re-mount on re-render) ──

const Field = ({ label, children, className = '' }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</Label>
    {children}
  </div>
);

const COUNTRY_CODES = [
  { code: '+91', label: 'IN +91' },
  { code: '+1', label: 'US +1' },
  { code: '+44', label: 'UK +44' },
  { code: '+971', label: 'AE +971' },
  { code: '+61', label: 'AU +61' },
  { code: '+65', label: 'SG +65' },
  { code: '+49', label: 'DE +49' },
  { code: '+33', label: 'FR +33' },
  { code: '+81', label: 'JP +81' },
  { code: '+86', label: 'CN +86' },
];

const CURRENCIES = [
  { code: 'INR', symbol: '\u20B9', label: 'INR (\u20B9)' },
  { code: 'USD', symbol: '$', label: 'USD ($)' },
  { code: 'GBP', symbol: '\u00A3', label: 'GBP (\u00A3)' },
  { code: 'EUR', symbol: '\u20AC', label: 'EUR (\u20AC)' },
  { code: 'AED', symbol: 'AED', label: 'AED' },
];

const TAG_COLORS = [
  'bg-violet-100 text-violet-700 border-violet-200',
  'bg-pink-100 text-pink-700 border-pink-200',
  'bg-blue-100 text-blue-700 border-blue-200',
  'bg-amber-100 text-amber-700 border-amber-200',
  'bg-teal-100 text-teal-700 border-teal-200',
  'bg-green-100 text-green-700 border-green-200',
  'bg-rose-100 text-rose-700 border-rose-200',
  'bg-indigo-100 text-indigo-700 border-indigo-200',
  'bg-orange-100 text-orange-700 border-orange-200',
  'bg-cyan-100 text-cyan-700 border-cyan-200',
  'bg-lime-100 text-lime-700 border-lime-200',
  'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200',
];

const PRESET_SUBJECTS = [
  'Physics', 'Chemistry', 'Mathematics', 'Biology', 'Computer Science',
  'English', 'Hindi', 'Economics', 'Business Studies', 'Accountancy',
  'History', 'Geography', 'Political Science', 'Psychology', 'Sociology',
  'Fine Arts', 'Physical Education', 'Informatics Practices', 'Engineering Drawing',
  'Environmental Science', 'Sanskrit', 'Music', 'Home Science',
];

const CLOUD_COLORS = [
  'bg-blue-50 text-blue-700 border-blue-200',
  'bg-green-50 text-green-700 border-green-200',
  'bg-sky-50 text-sky-700 border-sky-200',
  'bg-emerald-50 text-emerald-700 border-emerald-200',
  'bg-teal-50 text-teal-700 border-teal-200',
  'bg-cyan-50 text-cyan-700 border-cyan-200',
  'bg-indigo-50 text-indigo-700 border-indigo-200',
  'bg-violet-50 text-violet-700 border-violet-200',
];

function ExpectationTags({ label, value = [], onChange, suggestions = [] }) {
  const [input, setInput] = useState('');
  const tags = Array.isArray(value) ? value : (value ? value.split(',').map(s => s.trim()).filter(Boolean) : []);
  const addTag = (tag) => {
    const t = tag.trim();
    if (t && !tags.includes(t)) onChange([...tags, t]);
    setInput('');
  };
  const removeTag = (idx) => onChange(tags.filter((_, i) => i !== idx));
  const filtered = suggestions.filter(s => !tags.includes(s) && (!input || s.toLowerCase().includes(input.toLowerCase())));
  return (
    <div className="md:col-span-3 space-y-2">
      <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</Label>
      <div className="flex flex-wrap gap-1.5 p-2.5 min-h-[44px] border border-slate-200 rounded-md bg-white">
        {tags.map((t, i) => (
          <span key={i} className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border ${CLOUD_COLORS[i % CLOUD_COLORS.length]}`}>
            {t}
            <button type="button" onClick={() => removeTag(i)} className="ml-0.5 hover:opacity-70"><X size={12} /></button>
          </span>
        ))}
        <input className="flex-1 min-w-[140px] outline-none text-sm bg-transparent py-1" value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if ((e.key === 'Enter' || e.key === ',') && input.trim()) { e.preventDefault(); addTag(input); } }}
          placeholder={tags.length ? 'Add more...' : 'Type or select below...'} />
      </div>
      {filtered.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {filtered.slice(0, 10).map(s => (
            <button key={s} type="button" onClick={() => addTag(s)}
              className="px-3 py-1 rounded-full text-xs font-medium border border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100 hover:border-slate-300 transition-colors">
              + {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


function PhoneInput({ value, onChange, onBlur, placeholder, countryCode, onCountryCodeChange, error, ...props }) {
  const handleChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
    onChange({ target: { value: digits } });
  };
  return (
    <div>
      <div className="flex gap-0">
        <Select value={countryCode || '+91'} onValueChange={onCountryCodeChange}>
          <SelectTrigger className="w-24 rounded-r-none border-r-0 text-xs shrink-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {COUNTRY_CODES.map(c => (
              <SelectItem key={c.code} value={c.code} className="text-xs">{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          className="rounded-l-none"
          value={value}
          onChange={handleChange}
          onBlur={onBlur}
          placeholder={placeholder || '10-digit number'}
          maxLength={10}
          {...props}
        />
      </div>
      {value && value.length > 0 && value.length !== 10 && <p className="text-xs text-red-500 mt-1">Must be 10 digits</p>}
    </div>
  );
}

function CurrencyInput({ value, onChange, currency, onCurrencyChange, ...props }) {
  return (
    <div className="flex gap-0">
      <Select value={currency || 'INR'} onValueChange={onCurrencyChange}>
        <SelectTrigger className="w-24 rounded-r-none border-r-0 text-xs shrink-0">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {CURRENCIES.map(c => (
            <SelectItem key={c.code} value={c.code} className="text-xs">{c.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        className="rounded-l-none"
        type="text"
        inputMode="numeric"
        value={value}
        onChange={onChange}
        placeholder="Amount"
        {...props}
      />
    </div>
  );
}

function SubjectTagInput({ value, onChange }) {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);

  // Parse comma-separated string into array
  const tags = value ? value.split(',').map(s => s.trim()).filter(Boolean) : [];

  const getColorForTag = useCallback((tag) => {
    let hash = 0;
    for (let i = 0; i < tag.length; i++) hash = tag.charCodeAt(i) + ((hash << 5) - hash);
    return TAG_COLORS[Math.abs(hash) % TAG_COLORS.length];
  }, []);

  const addTag = useCallback((tag) => {
    const trimmed = tag.trim();
    if (!trimmed || tags.includes(trimmed)) return;
    const newTags = [...tags, trimmed];
    onChange(newTags.join(', '));
    setInputValue('');
    setShowSuggestions(false);
  }, [tags, onChange]);

  const removeTag = useCallback((tagToRemove) => {
    const newTags = tags.filter(t => t !== tagToRemove);
    onChange(newTags.join(', '));
  }, [tags, onChange]);

  const filteredSuggestions = PRESET_SUBJECTS.filter(
    s => s.toLowerCase().includes(inputValue.toLowerCase()) && !tags.includes(s)
  );

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (inputValue.trim()) addTag(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    }
  };

  // Close suggestions on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={wrapperRef} className="relative">
      <div
        className="flex flex-wrap items-center gap-1.5 p-2 min-h-[40px] border border-slate-200 rounded-md bg-white cursor-text focus-within:ring-2 focus-within:ring-slate-900 focus-within:ring-offset-1"
        onClick={() => inputRef.current?.focus()}
      >
        {tags.map(tag => (
          <span
            key={tag}
            className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${getColorForTag(tag)}`}
          >
            {tag}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); removeTag(tag); }}
              className="hover:opacity-70 transition-opacity"
            >
              <X size={12} />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          className="flex-1 min-w-[120px] outline-none text-sm bg-transparent placeholder:text-slate-400"
          value={inputValue}
          onChange={(e) => { setInputValue(e.target.value); setShowSuggestions(true); }}
          onFocus={() => setShowSuggestions(true)}
          onKeyDown={handleKeyDown}
          placeholder={tags.length === 0 ? 'Type subject & press Enter...' : 'Add more...'}
        />
      </div>
      {showSuggestions && inputValue && filteredSuggestions.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {filteredSuggestions.slice(0, 8).map(s => (
            <button
              key={s}
              type="button"
              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2"
              onMouseDown={(e) => { e.preventDefault(); addTag(s); }}
            >
              <span className={`inline-block w-2.5 h-2.5 rounded-full ${getColorForTag(s).split(' ')[0]}`} />
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Utility ──

function calculateAge(dob) {
  if (!dob) return '';
  const birth = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age >= 0 ? String(age) : '';
}

// ── Main Component ──

export default function NewLeadPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [masters, setMasters] = useState({ courses: [], stages: [], sources: [], triggers: [], examTypes: [], users: [] });
  const [duplicateWarning, setDuplicateWarning] = useState({ show: false, matches: [] });
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone_number: '', phone_country_code: '+91',
    alternate_phone_number: '', alt_phone_country_code: '+91',
    gender: '', date_of_birth: '', age: '', aadhaar_number: '',
    address_line_1: '', address_line_2: '', city: '', state: '', pin_code: '',
    source_id: '', trigger_id: '', current_stage_id: '', interested_course_id: '', interested_sub_course_id: '',
    assigned_counsellor_id: '', student_expectations: [], parent_expectations: [],
    parents: [
      { relation_type: 'father', full_name: '', contact_number: '', contact_country_code: '+91', email: '', occupation: '', annual_income: '', income_currency: 'INR' },
      { relation_type: 'mother', full_name: '', contact_number: '', contact_country_code: '+91', email: '', occupation: '', annual_income: '', income_currency: 'INR' }
    ],
    academics: [
      { academic_level: 'class_10', school_name: '', board_name: '', passing_year: '', percentage_or_cgpa: '', stream: '', major_subjects: '' },
      { academic_level: 'class_12', school_name: '', board_name: '', passing_year: '', percentage_or_cgpa: '', stream: '', major_subjects: '' }
    ],
    exam_scores: [],
    course_preferences: []
  });

  useEffect(() => {
    Promise.all([
      API.get('/masters/courses'), API.get('/masters/lead-stages'), API.get('/masters/lead-sources'),
      API.get('/masters/lead-triggers'), API.get('/masters/entrance-exam-types'), API.get('/users'),
      API.get('/sub-courses')
    ]).then(([c, st, so, tr, ex, u, sc]) => {
      setMasters({ courses: c.data, stages: st.data, sources: so.data, triggers: tr.data, examTypes: ex.data, users: u.data, subCourses: sc.data });
      const coldStage = st.data.find(s => s.name === 'Cold Lead');
      if (coldStage) setForm(p => ({ ...p, current_stage_id: coldStage.id }));
    }).catch(() => {});
  }, []);

  const set = (field, value) => setForm(p => ({ ...p, [field]: value }));

  const handleDobChange = (dob) => {
    const age = calculateAge(dob);
    setForm(p => ({ ...p, date_of_birth: dob, age }));
  };

  const setParent = (idx, field, value) => {
    setForm(p => {
      const parents = [...p.parents];
      parents[idx] = { ...parents[idx], [field]: value };
      return { ...p, parents };
    });
  };

  const setAcademic = (idx, field, value) => {
    setForm(p => {
      const academics = [...p.academics];
      academics[idx] = { ...academics[idx], [field]: value };
      return { ...p, academics };
    });
  };

  const addExamScore = () => setForm(p => ({
    ...p, exam_scores: [...p.exam_scores, { exam_type_id: '', score: '', rank: '', attempt_year: '' }]
  }));

  const setExamScore = (idx, field, value) => {
    setForm(p => {
      const scores = [...p.exam_scores];
      scores[idx] = { ...scores[idx], [field]: value };
      return { ...p, exam_scores: scores };
    });
  };

  const addCoursePref = () => setForm(p => ({
    ...p, course_preferences: [...p.course_preferences, { course_id: '', preference_type: 'interested', preference_order: p.course_preferences.length + 1 }]
  }));

  const setCoursePref = (idx, field, value) => {
    setForm(p => {
      const prefs = [...p.course_preferences];
      prefs[idx] = { ...prefs[idx], [field]: value };
      return { ...p, course_preferences: prefs };
    });
  };

  const checkDuplicate = async () => {
    if (!form.phone_number && !form.email) return;
    try {
      const res = await API.post('/leads/check-duplicate', { phone_number: form.phone_number, email: form.email });
      if (res.data.is_duplicate) setDuplicateWarning({ show: true, matches: res.data.matches });
    } catch {}
  };

  const handleSubmit = async (isDraft = false) => {
    if (!form.first_name || !form.phone_number) {
      toast.error('Full Name and Phone Number are required');
      return;
    }
    if (form.phone_number.length !== 10) {
      toast.error('Phone number must be 10 digits');
      return;
    }
    try {
      const payload = { ...form };
      // Prepend country codes to phone numbers for storage
      if (payload.phone_number && payload.phone_country_code) {
        payload.phone_number = `${payload.phone_country_code} ${payload.phone_number}`;
      }
      if (payload.alternate_phone_number && payload.alt_phone_country_code) {
        payload.alternate_phone_number = `${payload.alt_phone_country_code} ${payload.alternate_phone_number}`;
      }
      payload.parents = payload.parents.filter(p => p.full_name).map(p => {
        const parent = { ...p };
        if (parent.contact_number && parent.contact_country_code) {
          parent.contact_number = `${parent.contact_country_code} ${parent.contact_number}`;
        }
        if (parent.annual_income && parent.income_currency) {
          parent.annual_income = `${parent.income_currency} ${parent.annual_income}`;
        }
        return parent;
      });
      payload.academics = payload.academics.filter(a => a.school_name || a.board_name || a.percentage_or_cgpa);
      payload.exam_scores = payload.exam_scores.filter(e => e.exam_type_id);
      payload.course_preferences = payload.course_preferences.filter(c => c.course_id);
      if (isDraft) payload.lead_status = 'draft';
      const res = await API.post('/leads', payload);
      toast.success(`Lead ${res.data.lead_uid} created`);
      navigate(`/leads/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create lead');
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="new-lead-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>New Lead</h1>
          <p className="text-sm text-slate-500 mt-1">Create a new student lead record</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => handleSubmit(true)} data-testid="save-draft-btn"><Save size={14} className="mr-1" /> Save Draft</Button>
          <Button size="sm" onClick={() => handleSubmit(false)} className="bg-slate-900 hover:bg-slate-800" data-testid="submit-lead-btn"><Send size={14} className="mr-1" /> Submit Lead</Button>
        </div>
      </div>

      {/* Personal Details */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="pb-3"><CardTitle className="text-base font-semibold">Personal Details</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="First Name *">
            <Input value={form.first_name} onChange={e => set('first_name', e.target.value)} data-testid="first-name-input" />
          </Field>
          <Field label="Last Name">
            <Input value={form.last_name} onChange={e => set('last_name', e.target.value)} data-testid="last-name-input" />
          </Field>
          <Field label="Phone Number *">
            <PhoneInput
              value={form.phone_number}
              onChange={e => set('phone_number', e.target.value)}
              onBlur={checkDuplicate}
              countryCode={form.phone_country_code}
              onCountryCodeChange={v => set('phone_country_code', v)}
              data-testid="phone-input"
            />
          </Field>
          <Field label="Email">
            <Input type="email" value={form.email} onChange={e => set('email', e.target.value)} onBlur={checkDuplicate} data-testid="email-input" />
          </Field>
          <Field label="Alternate Phone">
            <PhoneInput
              value={form.alternate_phone_number}
              onChange={e => set('alternate_phone_number', e.target.value)}
              countryCode={form.alt_phone_country_code}
              onCountryCodeChange={v => set('alt_phone_country_code', v)}
            />
          </Field>
          <Field label="Gender">
            <Select value={form.gender || "unset"} onValueChange={v => set('gender', v === 'unset' ? '' : v)}>
              <SelectTrigger data-testid="gender-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent><SelectItem value="unset">Select</SelectItem><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
            </Select>
          </Field>
          <Field label="Date of Birth">
            <Input type="date" value={form.date_of_birth} onChange={e => handleDobChange(e.target.value)} data-testid="dob-input" />
          </Field>
          <Field label="Age (auto-calculated)">
            <Input type="text" value={form.age} readOnly className="bg-slate-50 cursor-not-allowed" placeholder="Set DOB to auto-calculate" data-testid="age-input" />
          </Field>
          <Field label="Aadhaar Number">
            <Input value={form.aadhaar_number} onChange={e => set('aadhaar_number', e.target.value)} />
          </Field>
          <Field label="Address Line 1" className="md:col-span-2">
            <Input value={form.address_line_1} onChange={e => set('address_line_1', e.target.value)} />
          </Field>
          <Field label="Address Line 2">
            <Input value={form.address_line_2} onChange={e => set('address_line_2', e.target.value)} />
          </Field>
          <Field label="City"><Input value={form.city} onChange={e => set('city', e.target.value)} /></Field>
          <Field label="State"><Input value={form.state} onChange={e => set('state', e.target.value)} /></Field>
          <Field label="Pin Code"><Input value={form.pin_code} onChange={e => set('pin_code', e.target.value)} /></Field>
        </CardContent>
      </Card>

      {/* Parent Details */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="pb-3"><CardTitle className="text-base font-semibold">Parent / Guardian Details</CardTitle></CardHeader>
        <CardContent className="space-y-6">
          {form.parents.map((parent, idx) => (
            <div key={idx}>
              <p className="text-sm font-medium text-slate-700 mb-3 capitalize">{parent.relation_type}</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Full Name">
                  <Input value={parent.full_name} onChange={e => setParent(idx, 'full_name', e.target.value)} data-testid={`parent-${idx}-name`} />
                </Field>
                <Field label="Contact">
                  <PhoneInput
                    value={parent.contact_number}
                    onChange={e => setParent(idx, 'contact_number', e.target.value)}
                    countryCode={parent.contact_country_code}
                    onCountryCodeChange={v => setParent(idx, 'contact_country_code', v)}
                  />
                </Field>
                <Field label="Email">
                  <Input type="email" value={parent.email} onChange={e => setParent(idx, 'email', e.target.value)} />
                </Field>
                <Field label="Occupation">
                  <Input value={parent.occupation} onChange={e => setParent(idx, 'occupation', e.target.value)} />
                </Field>
                <Field label="Annual Income">
                  <CurrencyInput
                    value={parent.annual_income}
                    onChange={e => setParent(idx, 'annual_income', e.target.value)}
                    currency={parent.income_currency}
                    onCurrencyChange={v => setParent(idx, 'income_currency', v)}
                  />
                </Field>
              </div>
              {idx < form.parents.length - 1 && <Separator className="mt-4" />}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Academic Details */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="pb-3"><CardTitle className="text-base font-semibold">Academic Details</CardTitle></CardHeader>
        <CardContent className="space-y-6">
          {form.academics.map((ac, idx) => (
            <div key={idx}>
              <p className="text-sm font-medium text-slate-700 mb-3">{ac.academic_level === 'class_10' ? 'Class 10' : 'Class 12'}</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="School Name"><Input value={ac.school_name} onChange={e => setAcademic(idx, 'school_name', e.target.value)} /></Field>
                <Field label="Board"><Input value={ac.board_name} onChange={e => setAcademic(idx, 'board_name', e.target.value)} /></Field>
                <Field label="Passing Year"><Input value={ac.passing_year} onChange={e => setAcademic(idx, 'passing_year', e.target.value)} /></Field>
                <Field label="Percentage/CGPA"><Input value={ac.percentage_or_cgpa} onChange={e => setAcademic(idx, 'percentage_or_cgpa', e.target.value)} /></Field>
                {ac.academic_level === 'class_12' && (
                  <>
                    <Field label="Stream"><Input value={ac.stream} onChange={e => setAcademic(idx, 'stream', e.target.value)} /></Field>
                    <Field label="Major Subjects" className="md:col-span-3">
                      <SubjectTagInput
                        value={ac.major_subjects}
                        onChange={v => setAcademic(idx, 'major_subjects', v)}
                      />
                    </Field>
                  </>
                )}
              </div>
              {idx < form.academics.length - 1 && <Separator className="mt-4" />}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Exam Scores */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base font-semibold">Entrance Exam Scores</CardTitle>
          <Button variant="outline" size="sm" onClick={addExamScore} data-testid="add-exam-btn">+ Add Exam</Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {form.exam_scores.length === 0 && <p className="text-sm text-slate-400">No exam scores added yet.</p>}
          {form.exam_scores.map((score, idx) => (
            <div key={idx} className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Field label="Exam Type">
                <Select value={score.exam_type_id || "unset"} onValueChange={v => setExamScore(idx, 'exam_type_id', v === 'unset' ? '' : v)}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent><SelectItem value="unset">Select</SelectItem>{masters.examTypes.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Score"><Input value={score.score} onChange={e => setExamScore(idx, 'score', e.target.value)} /></Field>
              <Field label="Rank"><Input value={score.rank} onChange={e => setExamScore(idx, 'rank', e.target.value)} /></Field>
              <Field label="Year"><Input value={score.attempt_year} onChange={e => setExamScore(idx, 'attempt_year', e.target.value)} /></Field>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Course Preferences */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base font-semibold">Course Preferences</CardTitle>
          <Button variant="outline" size="sm" onClick={addCoursePref} data-testid="add-course-pref-btn">+ Add Preference</Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {form.course_preferences.length === 0 && <p className="text-sm text-slate-400">No course preferences added yet.</p>}
          {form.course_preferences.map((pref, idx) => (
            <div key={idx} className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label={`Preference #${idx + 1}`}>
                <Select value={pref.course_id || "unset"} onValueChange={v => setCoursePref(idx, 'course_id', v === 'unset' ? '' : v)}>
                  <SelectTrigger><SelectValue placeholder="Select course" /></SelectTrigger>
                  <SelectContent><SelectItem value="unset">Select</SelectItem>{masters.courses.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Status">
                <Select value={pref.preference_type} onValueChange={v => setCoursePref(idx, 'preference_type', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="interested">Interested</SelectItem><SelectItem value="applied">Applied</SelectItem>
                    <SelectItem value="qualified">Qualified</SelectItem><SelectItem value="admitted">Admitted</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Source, Trigger, Assignment */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="pb-3"><CardTitle className="text-base font-semibold">Source, Assignment & Expectations</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Lead Source">
            <Select value={form.source_id || "unset"} onValueChange={v => set('source_id', v === 'unset' ? '' : v)}>
              <SelectTrigger data-testid="source-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent><SelectItem value="unset">Select</SelectItem>{masters.sources.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Trigger">
            <Select value={form.trigger_id || "unset"} onValueChange={v => set('trigger_id', v === 'unset' ? '' : v)}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent><SelectItem value="unset">Select</SelectItem>{masters.triggers.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Interested Course">
            <Select value={form.interested_course_id || "unset"} onValueChange={v => { set('interested_course_id', v === 'unset' ? '' : v); set('interested_sub_course_id', ''); }}>
              <SelectTrigger data-testid="interested-course-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent><SelectItem value="unset">Select</SelectItem>{masters.courses.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Sub-Course / Specialization">
            <Select value={form.interested_sub_course_id || "unset"} onValueChange={v => set('interested_sub_course_id', v === 'unset' ? '' : v)} disabled={!form.interested_course_id}>
              <SelectTrigger data-testid="sub-course-select"><SelectValue placeholder={form.interested_course_id ? "Select specialization" : "Select course first"} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unset">None</SelectItem>
                {(masters.subCourses || []).filter(s => s.course_id === form.interested_course_id).map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Current Stage">
            <Select value={form.current_stage_id || "unset"} onValueChange={v => set('current_stage_id', v === 'unset' ? '' : v)}>
              <SelectTrigger data-testid="stage-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent><SelectItem value="unset">Select</SelectItem>{masters.stages.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Assigned Counsellor">
            <Select value={form.assigned_counsellor_id || "unset"} onValueChange={v => set('assigned_counsellor_id', v === 'unset' ? '' : v)}>
              <SelectTrigger data-testid="counsellor-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent><SelectItem value="unset">Select</SelectItem>{masters.users.filter(u => ['admin', 'counsellor', 'admission_head'].includes(u.role)).map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <div />
          <ExpectationTags label="Student Expectations" value={form.student_expectations} onChange={v => set('student_expectations', v)}
            suggestions={['Placement Assistance', 'Industry Exposure', 'Internship Opportunities', 'Research Projects', 'Hostel Facility', 'Scholarship', 'Sports Facilities', 'Campus Life', 'International Exposure', 'Skill Development', 'Coding Bootcamp', 'Entrepreneurship Support', 'Mentorship', 'Certification Courses']} />
          <ExpectationTags label="Parent Expectations" value={form.parent_expectations} onChange={v => set('parent_expectations', v)}
            suggestions={['Good Placement Record', 'Safe Campus', 'Quality Faculty', 'Affordable Fees', 'Accreditation', 'Proximity to Home', 'Hostel Security', 'Career Guidance', 'Discipline', 'Reputation', 'Alumni Network', 'Transport Facility', 'Scholarship Options', 'Regular Updates']} />
        </CardContent>
      </Card>

      {/* Duplicate Warning Dialog */}
      <Dialog open={duplicateWarning.show} onOpenChange={v => setDuplicateWarning({ show: v, matches: duplicateWarning.matches })}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-amber-700"><AlertTriangle size={18} /> Duplicate Lead Detected</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">A lead with the same phone number or email already exists:</p>
            {duplicateWarning.matches.map(m => (
              <div key={m.id} className="p-3 bg-amber-50 rounded-lg text-sm">
                <p className="font-medium">{m.full_name} ({m.lead_uid})</p>
                <p className="text-xs text-slate-500">{m.phone_number} &middot; {m.email}</p>
              </div>
            ))}
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setDuplicateWarning({ show: false, matches: [] })}>Close</Button>
              <Button onClick={() => { set('is_duplicate_flag', true); setDuplicateWarning({ show: false, matches: [] }); }} className="bg-amber-600 hover:bg-amber-700">Continue Anyway</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
